import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useFinance } from '../context/FinanceContext';
import { 
  Bot, 
  X, 
  Send, 
  Sparkles, 
  CornerDownLeft, 
  Loader2,
  TrendingUp,
  Receipt,
  PiggyBank,
  BrainCircuit,
  Flame,
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Target,
  Trophy,
  Zap,
  ArrowRight,
  RotateCcw,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface TrackedGoal {
  id: string;
  title: string;
  category: 'Portfolio' | 'Budget' | 'Savings' | 'Debt';
  completed: boolean;
  targetDate?: string;
  notes?: string;
  createdAt: string;
}

const DEFAULT_TRACKED_GOALS: TrackedGoal[] = [
  {
    id: 'goal-1',
    title: 'Deploy at least $500 monthly into primary growth bucket',
    category: 'Portfolio',
    completed: false,
    createdAt: new Date().toISOString()
  },
  {
    id: 'goal-2',
    title: 'Maintain living expenses below 70% of total budget',
    category: 'Budget',
    completed: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'goal-3',
    title: 'Rebalance high-concentration bucket to under 40% total portfolio',
    category: 'Portfolio',
    completed: false,
    createdAt: new Date().toISOString()
  }
];

const FinanceChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'tracker'>('chat');
  const [selectedModel, setSelectedModel] = useState<'gemini-3.5-flash' | 'gemini-3.1-flash-lite' | 'gemini-3.1-pro-preview'>('gemini-3.5-flash');
  
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem('wealthy_chat_history');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        console.error('Failed to parse chat history', e);
      }
    }
    return [
      {
        role: 'assistant',
        content: "Greetings! I am **Wealthy**, your executive Personal Wealth Coach & Portfolio Architect. 👑\n\nI have loaded your live portfolio, balance sheet, cash flows, and budget analytics. Ask me for tailored **wealth growth suggestions**, **portfolio rebalancing strategies**, or let me evaluate your **tracked milestones** and deliver an energizing **motivational boost** to accelerate your financial independence! 🚀",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ];
  });

  const [inputMsg, setInputMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Tracked Results / Action Plan
  const [trackedGoals, setTrackedGoals] = useState<TrackedGoal[]>(() => {
    const saved = localStorage.getItem('wealthy_tracked_milestones');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.error('Failed to parse tracked goals', e);
      }
    }
    return DEFAULT_TRACKED_GOALS;
  });

  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState<'Portfolio' | 'Budget' | 'Savings' | 'Debt'>('Portfolio');

  const { 
    expenses, 
    budgets, 
    investments, 
    investmentCategories, 
    balanceSheet, 
    cashFlowEvents, 
    currency, 
    convertToDisplay,
    isDarkMode 
  } = useFinance();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Save chat history
  useEffect(() => {
    localStorage.setItem('wealthy_chat_history', JSON.stringify(messages));
  }, [messages]);

  // Save tracked goals
  useEffect(() => {
    localStorage.setItem('wealthy_tracked_milestones', JSON.stringify(trackedGoals));
  }, [trackedGoals]);

  // Listen for external open triggers from navbar
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('open-wealthy-chat', handleOpenChat);
    return () => window.removeEventListener('open-wealthy-chat', handleOpenChat);
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    if (activeTab === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, activeTab]);

  // Comprehensive portfolio analysis & context construction
  const portfolioSummary = useMemo(() => {
    const totalInvestments = investmentCategories.reduce((acc, curr) => acc + curr.currentValue, 0);
    const totalPrincipal = investments.reduce((acc, curr) => acc + curr.investedAmount, 0);
    const netGains = totalInvestments - totalPrincipal;
    const portfolioRoi = totalPrincipal > 0 ? (netGains / totalPrincipal) * 100 : 0;

    const manualAssets = balanceSheet.filter(i => i.type === 'Asset').reduce((acc, curr) => acc + curr.value, 0);
    const liabilities = balanceSheet.filter(i => i.type === 'Liability').reduce((acc, curr) => acc + curr.value, 0);
    
    const assetsDisp = convertToDisplay(manualAssets + totalInvestments);
    const liabilitiesDisp = convertToDisplay(liabilities);
    const netWorthDisp = assetsDisp - liabilitiesDisp;
    const debtToAssetRatio = assetsDisp > 0 ? (liabilitiesDisp / assetsDisp) * 100 : 0;

    const expTotal = expenses.reduce((acc, curr) => acc + curr.amount, 0);
    const budgetTotal = budgets.reduce((acc, curr) => acc + curr.budget, 0);
    const savingsVelocity = budgetTotal > 0 ? Math.max(0, ((budgetTotal - expTotal) / budgetTotal) * 100) : 0;

    // Category diagnostics
    const categoriesWithStats = investmentCategories.map(c => {
      const catInvested = investments
        .filter(inv => inv.category === c.name)
        .reduce((sum, inv) => sum + inv.investedAmount, 0);
      const catRoi = catInvested > 0 ? ((c.currentValue - catInvested) / catInvested) * 100 : 0;
      const weight = totalInvestments > 0 ? (c.currentValue / totalInvestments) * 100 : 0;
      const gap = Math.max(0, c.objective - c.currentValue);
      return {
        name: c.name,
        currentValue: convertToDisplay(c.currentValue),
        invested: convertToDisplay(catInvested),
        target: convertToDisplay(c.objective),
        roi: catRoi,
        weight,
        gap: convertToDisplay(gap)
      };
    });

    const alphaDriver = [...categoriesWithStats].sort((a, b) => b.roi - a.roi)[0] || null;
    const laggard = [...categoriesWithStats].sort((a, b) => a.roi - b.roi)[0] || null;
    const topConcentrated = [...categoriesWithStats].sort((a, b) => b.weight - a.weight)[0] || null;

    // Budget overruns
    const overBudgetCategories = budgets.filter(b => {
      const spent = expenses.filter(e => e.category === b.category).reduce((s, e) => s + e.amount, 0);
      return spent > b.budget;
    }).map(b => {
      const spent = expenses.filter(e => e.category === b.category).reduce((s, e) => s + e.amount, 0);
      return {
        category: b.category,
        spent: convertToDisplay(spent),
        budget: convertToDisplay(b.budget),
        over: convertToDisplay(spent - b.budget)
      };
    });

    return {
      netWorthDisp,
      assetsDisp,
      liabilitiesDisp,
      debtToAssetRatio,
      totalInvestments: convertToDisplay(totalInvestments),
      totalPrincipal: convertToDisplay(totalPrincipal),
      netGains: convertToDisplay(netGains),
      portfolioRoi,
      categoriesWithStats,
      alphaDriver,
      laggard,
      topConcentrated,
      expTotal: convertToDisplay(expTotal),
      budgetTotal: convertToDisplay(budgetTotal),
      savingsVelocity,
      overBudgetCategories
    };
  }, [investmentCategories, investments, balanceSheet, expenses, budgets, convertToDisplay]);

  // Construct context-aware system instruction
  const generateSystemInstruction = () => {
    const categoriesText = portfolioSummary.categoriesWithStats.map(c => 
      `- Bucket "${c.name}": Value ${currency}${c.currentValue.toLocaleString()} (${c.weight.toFixed(1)}% of portfolio) | Invested ${currency}${c.invested.toLocaleString()} | Lifetime ROI: ${c.roi >= 0 ? '+' : ''}${c.roi.toFixed(1)}% | Goal: ${currency}${c.target.toLocaleString()} (Gap: ${currency}${c.gap.toLocaleString()})`
    ).join('\n');

    const goalsText = trackedGoals.map(g => 
      `- [${g.completed ? 'COMPLETED' : 'IN PROGRESS'}] ${g.title} (${g.category})`
    ).join('\n');

    const overBudgetText = portfolioSummary.overBudgetCategories.length > 0
      ? portfolioSummary.overBudgetCategories.map(o => `- Over Budget in "${o.category}": Spent ${currency}${o.spent.toLocaleString()} vs Limit ${currency}${o.budget.toLocaleString()} (Over by +${currency}${o.over.toLocaleString()})`).join('\n')
      : "None - all spending is strictly within budget limits!";

    return `You are "Wealthy", the personal Chief Financial Officer, Portfolio Architect, and Master Wealth Coach.
Your mission is to guide the user to compounding financial freedom, provide clear tactical wealth growth suggestions, keep track of their commitments and results, and deliver high-energy motivational speeches that sharpen their discipline.

CURRENT LIVE PORTFOLIO & FINANCIAL STATE (All figures in ${currency}):
- Total Net Worth: ${currency}${portfolioSummary.netWorthDisp.toLocaleString()}
- Total Assets: ${currency}${portfolioSummary.assetsDisp.toLocaleString()}
- Total Liabilities / Debt: ${currency}${portfolioSummary.liabilitiesDisp.toLocaleString()} (Debt-to-Asset: ${portfolioSummary.debtToAssetRatio.toFixed(1)}%)
- Investment Portfolio Valuation: ${currency}${portfolioSummary.totalInvestments.toLocaleString()}
- Cumulative Invested Capital: ${currency}${portfolioSummary.totalPrincipal.toLocaleString()}
- Net Lifetime Gain/Alpha: ${currency}${portfolioSummary.netGains >= 0 ? '+' : ''}${portfolioSummary.netGains.toLocaleString()} (${portfolioSummary.portfolioRoi >= 0 ? '+' : ''}${portfolioSummary.portfolioRoi.toFixed(1)}% lifetime return)
- Top Alpha Performing Bucket: ${portfolioSummary.alphaDriver ? `"${portfolioSummary.alphaDriver.name}" at +${portfolioSummary.alphaDriver.roi.toFixed(1)}% ROI` : 'None indexed'}
- Highest Concentration Asset: ${portfolioSummary.topConcentrated ? `"${portfolioSummary.topConcentrated.name}" at ${portfolioSummary.topConcentrated.weight.toFixed(1)}% allocation` : 'None indexed'}
- Recent Total Expenses: ${currency}${portfolioSummary.expTotal.toLocaleString()} vs Budget Total: ${currency}${portfolioSummary.budgetTotal.toLocaleString()} (Savings Rate Velocity: ${portfolioSummary.savingsVelocity.toFixed(1)}%)
- Over-Budget Status:
${overBudgetText}

ACTIVE ASSET BUCKETS:
${categoriesText || "No active investment positions logged."}

USER'S CURRENT TRACKED FINANCIAL GOALS & COMMITMENTS:
${goalsText || "No tracked goals currently recorded."}

CORE INSTRUCTIONS & COACHING CONSTITUTION:
1. ALWAYS reference and anchor your answers in their real live numbers above.
2. PROVIDE SPECIFIC WEALTH GROWTH SUGGESTIONS:
   - Advise on compounding rebalancing (e.g. taking profit from over-concentrated buckets, channeling DCA capital into under-allocated or high-conviction assets).
   - Identify concrete dollar amounts from spending leaks or over-budget categories to redirect into monthly compounding deployment.
   - Project realistic compounding scenarios (e.g., "If you deploy +${currency}500/month at 8% annual return over 3 years, you accumulate...").
3. KEEP TRACK OF RESULTS:
   - Acknowledge their tracked goals and hold them accountable with executive praise or constructive challenge.
   - If they achieve a milestone or stay within budget, applaud their momentum.
   - If they propose a new commitment, explicitly validate it and encourage them to log it in their Tracked Goals tab.
4. DELIVER POWERFUL MOTIVATIONAL SPEECH & MINDSET:
   - In every response, include an uplifting, stoic, and relentless wealth mindset principle.
   - Remind them that true financial freedom is forged through consistency, emotional mastery during market volatility, and relentless capital deployment.
   - Conclude your answer with an inspiring "🌟 Wealth Principle" blockquote.
5. FORMATTING:
   - Use crisp headings (###), bold numbers, structured bullet points, and clean comparison tables when evaluating asset weights.
   - Keep answers punchy, executive-level, and easy to read on mobile and desktop.`;
  };

  const handleSendMessage = async (msgText = inputMsg) => {
    if (!msgText.trim()) return;

    const userMessage: ChatMessage = { 
      role: 'user', 
      content: msgText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    const chatHistory = [...messages, userMessage];
    
    setMessages(chatHistory);
    setInputMsg('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: chatHistory,
          systemInstruction: generateSystemInstruction(),
          model: selectedModel,
        }),
      });

      if (!response.ok) {
        let errorMessage = "";
        try {
          const textPayload = await response.text();
          try {
            const errData = JSON.parse(textPayload);
            errorMessage = errData.error || errData.message;
          } catch {
            errorMessage = `HTTP ${response.status}: ${textPayload.substring(0, 150)}`;
          }
        } catch {
          errorMessage = `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage || 'Server error generating response');
      }

      const data = await response.json();
      setMessages(prev => [
        ...prev, 
        { 
          role: 'assistant', 
          content: data.text || "I have analyzed your portfolio, but could not synthesize advice. Please prompt again!",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } catch (err: any) {
      console.error(err);
      setMessages(prev => [
        ...prev, 
        { 
          role: 'assistant', 
          content: `⚠️ **Advisory Notice:** ${err.message || "Failed to reach AI engine. Ensure your GEMINI_API_KEY is configured in AI Studio secrets."}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Goal Tracker actions
  const toggleGoal = (id: string) => {
    setTrackedGoals(prev => prev.map(g => g.id === id ? { ...g, completed: !g.completed } : g));
  };

  const removeGoal = (id: string) => {
    setTrackedGoals(prev => prev.filter(g => g.id !== id));
  };

  const handleAddGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoalTitle.trim()) return;
    const newG: TrackedGoal = {
      id: `goal-${Date.now()}`,
      title: newGoalTitle.trim(),
      category: newGoalCategory,
      completed: false,
      createdAt: new Date().toISOString()
    };
    setTrackedGoals(prev => [newG, ...prev]);
    setNewGoalTitle('');
  };

  const completedGoalsCount = trackedGoals.filter(g => g.completed).length;
  const goalCompletionPct = trackedGoals.length > 0 ? Math.round((completedGoalsCount / trackedGoals.length) * 100) : 0;

  // Ask Wealthy to audit tracked goals
  const handleAuditGoalsInChat = () => {
    setActiveTab('chat');
    handleSendMessage(`Please review my current tracked wealth goals (${completedGoalsCount}/${trackedGoals.length} completed). Evaluate whether these targets align with my portfolio and give me constructive feedback and motivational encouragement to achieve the rest.`);
  };

  // Generate Motivational Boost
  const handleMotivationalBoost = () => {
    setActiveTab('chat');
    handleSendMessage(`Wealthy, give me an intense, inspiring motivational speech based on my current portfolio numbers (${currency}${portfolioSummary.totalInvestments.toLocaleString()} investments, ${currency}${portfolioSummary.netWorthDisp.toLocaleString()} net worth) to reinforce my financial discipline and compound my wealth!`);
  };

  // Structured custom markdown parser
  const renderMessageText = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Check if list item
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        const content = line.trim().substring(2);
        return (
          <li key={idx} className="ml-3 sm:ml-4 list-disc pl-1 mb-1.5 text-xs text-slate-700 dark:text-slate-200">
            {parseInlineStyles(content)}
          </li>
        );
      }
      // Check if quote / wealth principle
      if (line.trim().startsWith('>')) {
        return (
          <div key={idx} className="my-2.5 p-3 rounded-2xl bg-amber-500/10 border-l-4 border-amber-500 text-xs font-semibold text-amber-900 dark:text-amber-200">
            {parseInlineStyles(line.replace(/^>\s*/, ''))}
          </div>
        );
      }
      // Check if table row
      if (line.trim().startsWith('|') && line.includes('---')) {
        return null;
      }
      if (line.trim().startsWith('|')) {
        const cols = line.split('|').map(c => c.trim()).filter(Boolean);
        return (
          <div key={idx} className="grid grid-cols-2 sm:grid-cols-3 gap-2 py-1.5 px-2 border-b border-slate-100 dark:border-white/5 text-[11px]">
            {cols.map((col, cidx) => (
              <span key={cidx} className={cidx === 0 ? 'font-black text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}>
                {parseInlineStyles(col)}
              </span>
            ))}
          </div>
        );
      }
      // Check if headers
      if (line.trim().startsWith('###')) {
        return <h4 key={idx} className="text-xs font-black uppercase text-blue-600 dark:text-blue-400 mt-3 mb-1.5 tracking-wider">{parseInlineStyles(line.replace('###', ''))}</h4>;
      }
      if (line.trim().startsWith('##')) {
        return <h3 key={idx} className="text-xs font-black text-slate-900 dark:text-white mt-3.5 mb-1.5 tracking-tight border-b pb-1 border-slate-100 dark:border-white/5">{parseInlineStyles(line.replace('##', ''))}</h3>;
      }
      if (line.trim().startsWith('#')) {
        return <h2 key={idx} className="text-sm font-black text-slate-900 dark:text-white mt-4 mb-2 tracking-tighter">{parseInlineStyles(line.replace('#', ''))}</h2>;
      }
      // Empty line
      if (!line.trim()) {
        return <div key={idx} className="h-1.5" />;
      }
      return <p key={idx} className="leading-relaxed text-xs text-slate-700 dark:text-slate-200 mb-1.5 last:mb-0">{parseInlineStyles(line)}</p>;
    });
  };

  const parseInlineStyles = (part: string) => {
    const elements: React.ReactNode[] = [];
    let remaining = part;
    let keyIndex = 0;

    while (remaining.length > 0) {
      const boldIndex = remaining.indexOf('**');
      const codeIndex = remaining.indexOf('`');

      const firstIndex = [boldIndex, codeIndex].filter(i => i !== -1).sort((a,b) => a-b)[0];

      if (firstIndex === undefined) {
        elements.push(<span key={keyIndex++}>{remaining}</span>);
        break;
      }

      if (firstIndex > 0) {
        elements.push(<span key={keyIndex++}>{remaining.substring(0, firstIndex)}</span>);
        remaining = remaining.substring(firstIndex);
      }

      if (remaining.startsWith('**')) {
        const nextBold = remaining.indexOf('**', 2);
        if (nextBold !== -1) {
          elements.push(<strong key={keyIndex++} className="font-bold text-slate-900 dark:text-white">{remaining.substring(2, nextBold)}</strong>);
          remaining = remaining.substring(nextBold + 2);
        } else {
          elements.push(<span key={keyIndex++}>**</span>);
          remaining = remaining.substring(2);
        }
      } else if (remaining.startsWith('`')) {
        const nextCode = remaining.indexOf('`', 1);
        if (nextCode !== -1) {
          elements.push(<code key={keyIndex++} className="px-1 py-0.5 bg-slate-100 dark:bg-white/10 rounded font-mono text-[10px] text-blue-600 dark:text-blue-300">{remaining.substring(1, nextCode)}</code>);
          remaining = remaining.substring(nextCode + 1);
        } else {
          elements.push(<span key={keyIndex++}>`</span>);
          remaining = remaining.substring(1);
        }
      }
    }
    return elements;
  };

  const starterQuestions = [
    { 
      label: '🚀 Wealth Growth Blueprint', 
      q: 'Provide 3 concrete recommendations to accelerate my portfolio growth and compound my net worth over the next 12 months based on my current positions.' 
    },
    { 
      label: '📊 Portfolio Rebalancing', 
      q: 'Analyze my investment bucket allocation weights. Which categories are over-concentrated or lagging, and how should I rebalance them?' 
    },
    { 
      label: '💡 Find $500/mo Extra Capital', 
      q: 'Audit my recent expenses and budgets. Where can I optimize or trim spending to unlock an extra $500/month for automated investment deployment?' 
    },
    { 
      label: '⚡ Motivational Mindset Speech', 
      q: 'Give me a powerful, executive motivational speech to stay fiercely disciplined with my money, ignore emotional market noise, and build generational wealth!' 
    }
  ];

  return (
    <>
      {/* Floating Action Trigger Button */}
      <button
        id="wealthy-chatbot-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[45] p-4 font-bold text-white rounded-full bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 border-2 border-blue-500/30 ${
          isOpen ? 'rotate-90 bg-rose-600 scale-95 border-rose-500/30' : ''
        }`}
        title="Ask Wealthy - AI Personal Wealth Coach"
        aria-label="Toggle Ask Wealthy Chat"
      >
        {isOpen ? <X size={24} /> : (
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Bot size={22} className="text-white" />
              <Sparkles size={13} className="text-yellow-400 absolute -top-1.5 -right-1.5 animate-bounce" />
            </div>
            <span className="hidden sm:inline text-xs tracking-wider uppercase font-black">Ask Wealthy</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
        )}
      </button>

      {/* Chatbot Window */}
      {isOpen && (
        <div 
          id="wealthy-chatbot-win"
          className={`fixed bottom-24 right-4 sm:right-6 z-[45] w-[94vw] sm:w-[460px] h-[640px] max-h-[86vh] rounded-[32px] shadow-2xl flex flex-col overflow-hidden border transition-all duration-300 animate-in slide-in-from-bottom-5 duration-200 ${
            isDarkMode 
              ? 'bg-[#111E33] border-[#1A2940] text-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.7)]' 
              : 'bg-white border-slate-200 text-slate-900 shadow-2xl'
          }`}
        >
          {/* Header */}
          <div className="p-4 sm:p-5 flex flex-col gap-3 border-b bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 text-white border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-blue-600 text-white shadow-md shadow-blue-500/30">
                  <BrainCircuit size={20} className="animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-black tracking-tight">Ask Wealthy</h3>
                    <span className="text-[9px] font-black uppercase tracking-wider bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full border border-blue-400/20">
                      Chief Wealth Coach
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold tracking-wider mt-0.5 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping inline-block" />
                    Portfolio Context Connected ({portfolioSummary.categoriesWithStats.length} Buckets)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleMotivationalBoost}
                  className="p-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-400/30 transition-all flex items-center gap-1 text-[10px] font-black"
                  title="Generate instant motivational boost"
                >
                  <Flame size={14} className="text-amber-400" />
                  <span className="hidden xs:inline">Motivation</span>
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-all"
                  aria-label="Close Chat Window"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Sub-Header: Tabs & Model Selector */}
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/10">
              {/* Tab Navigation */}
              <div className="flex items-center bg-white/10 p-0.5 rounded-xl text-xs font-bold">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`px-3 py-1 rounded-lg transition-all ${activeTab === 'chat' ? 'bg-blue-600 text-white shadow-xs font-black' : 'text-slate-300 hover:text-white'}`}
                >
                  💬 Chat
                </button>
                <button
                  onClick={() => setActiveTab('tracker')}
                  className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'tracker' ? 'bg-blue-600 text-white shadow-xs font-black' : 'text-slate-300 hover:text-white'}`}
                >
                  🎯 Tracked Goals
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.2 rounded-full">
                    {completedGoalsCount}/{trackedGoals.length}
                  </span>
                </button>
              </div>

              {/* Gemini Model Selector */}
              <div className="flex items-center gap-1">
                <select
                  value={selectedModel}
                  onChange={(e: any) => setSelectedModel(e.target.value)}
                  className="bg-white/10 border border-white/15 text-slate-200 text-[10px] font-black rounded-lg px-2 py-1 outline-none cursor-pointer focus:ring-1 focus:ring-blue-400"
                  title="Select Gemini Engine"
                >
                  <option value="gemini-3.5-flash" className="bg-slate-900 text-white">Gemini 3.5 Flash (General)</option>
                  <option value="gemini-3.1-flash-lite" className="bg-slate-900 text-white">Gemini 3.1 Flash-Lite (Fast)</option>
                  <option value="gemini-3.1-pro-preview" className="bg-slate-900 text-white">Gemini 3.1 Pro (Complex)</option>
                </select>
              </div>
            </div>
          </div>

          {/* TAB 1: CONVERSATION THREAD */}
          {activeTab === 'chat' && (
            <>
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 custom-scrollbar">
                {messages.map((m, idx) => (
                  <div 
                    key={idx}
                    className={`flex gap-2.5 max-w-[88%] ${m.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}
                  >
                    {m.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black shrink-0 text-[10px] shadow-sm mt-0.5">
                        <Bot size={14} />
                      </div>
                    )}
                    <div 
                      className={`rounded-2xl p-4 text-xs font-medium leading-relaxed shadow-xs ${
                        m.role === 'user'
                          ? 'bg-blue-600 text-white ml-auto rounded-tr-none'
                          : isDarkMode
                            ? 'bg-[#162340] border border-[#1A2940] text-slate-100 rounded-tl-none'
                            : 'bg-slate-50 border border-slate-200 rounded-tl-none text-slate-800'
                      }`}
                    >
                      <div className="space-y-1.5 font-sans">
                        {renderMessageText(m.content)}
                      </div>
                      {m.timestamp && (
                        <div className={`text-[9px] font-bold mt-2 text-right ${m.role === 'user' ? 'text-blue-200' : 'text-slate-400'}`}>
                          {m.timestamp}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-2.5 max-w-[85%]">
                    <div className="w-7 h-7 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black shrink-0 text-[10px] mt-0.5">
                      <Bot size={14} />
                    </div>
                    <div className={`rounded-2xl p-4 text-xs font-semibold shadow-xs flex items-center gap-2.5 ${
                      isDarkMode ? 'bg-[#162340] border border-[#1A2940] text-slate-300' : 'bg-slate-50 border border-slate-200 text-slate-600'
                    }`}>
                      <Loader2 size={15} className="animate-spin text-blue-500" />
                      <span>Synthesizing portfolio intelligence...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Starter Suggestions */}
              {messages.length <= 2 && !isLoading && (
                <div className="px-4 sm:px-5 pb-3">
                  <p className="text-[9.5px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Sparkles size={11} className="text-yellow-500" /> Coaching Actions
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {starterQuestions.map((sq, sIdx) => (
                      <button
                        key={sIdx}
                        onClick={() => handleSendMessage(sq.q)}
                        className={`flex items-center justify-between p-2.5 text-left rounded-xl border text-[11px] font-bold transition-all hover:scale-[1.01] active:scale-[0.98] ${
                          isDarkMode 
                            ? 'border-[#1A2940] bg-[#162340] hover:bg-[#1A2940] text-slate-200' 
                            : 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800'
                        }`}
                      >
                        <span className="truncate">{sq.label}</span>
                        <ChevronRight size={12} className="text-slate-400 shrink-0 ml-1" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Box */}
              <div className={`p-3 sm:p-4 border-t ${isDarkMode ? 'border-[#1A2940] bg-[#111E33]' : 'border-slate-200 bg-white'}`}>
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                  }}
                  className="relative flex items-center"
                >
                  <input
                    type="text"
                    value={inputMsg}
                    onChange={(e) => setInputMsg(e.target.value)}
                    placeholder="Ask Wealthy for suggestions, portfolio rebalancing..."
                    disabled={isLoading}
                    className={`w-full py-3.5 pl-4 pr-12 rounded-2xl text-xs font-bold outline-none border focus:ring-2 focus:ring-blue-500 transition-all ${
                      isDarkMode 
                        ? 'border-[#1A2940] bg-[#162340] text-white placeholder-slate-400' 
                        : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400'
                    }`}
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !inputMsg.trim()}
                    className={`absolute right-2 p-2.5 rounded-xl transition-all font-black text-white ${
                      inputMsg.trim() && !isLoading 
                        ? 'bg-blue-600 hover:bg-blue-500 hover:scale-[1.05] active:scale-95 shadow-md shadow-blue-500/30' 
                        : 'bg-slate-300 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                    }`}
                    title="Send message"
                  >
                    <Send size={14} />
                  </button>
                </form>

                <div className="flex justify-between items-center mt-2 px-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("Restart chat history with Wealthy?")) {
                          setMessages([
                            {
                              role: 'assistant',
                              content: "Chat history cleared. I'm ready to evaluate your latest financial trajectory!",
                              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            }
                          ]);
                        }
                      }}
                      className="text-[9px] font-black text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 uppercase tracking-wider flex items-center gap-1"
                    >
                      <RotateCcw size={9} /> Reset
                    </button>
                    <span className="text-slate-300 dark:text-slate-600">·</span>
                    <button
                      type="button"
                      onClick={handleMotivationalBoost}
                      className="text-[9px] font-black text-amber-500 hover:text-amber-600 dark:hover:text-amber-400 uppercase tracking-wider flex items-center gap-1"
                    >
                      <Zap size={9} /> Boost Mindset
                    </button>
                  </div>

                  <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1">
                    <CornerDownLeft size={8} /> Enter to Send
                  </span>
                </div>
              </div>
            </>
          )}

          {/* TAB 2: TRACKED RESULTS & MILESTONES */}
          {activeTab === 'tracker' && (
            <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
              {/* Progress Summary Card */}
              <div className={`p-4 rounded-2xl border ${
                isDarkMode ? 'bg-[#162340] border-[#1A2940]' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy size={18} className="text-amber-400" />
                    <span className="text-xs font-black uppercase tracking-wider">Commitment Accomplishment</span>
                  </div>
                  <span className="text-sm font-black text-blue-500 font-mono">
                    {goalCompletionPct}%
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all duration-500"
                    style={{ width: `${goalCompletionPct}%` }}
                  />
                </div>
                <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mt-2">
                  <span>{completedGoalsCount} of {trackedGoals.length} milestones reached</span>
                  <span>{trackedGoals.length - completedGoalsCount} active targets</span>
                </div>

                <button
                  onClick={handleAuditGoalsInChat}
                  className="w-full mt-3 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 shadow-xs"
                >
                  <Bot size={13} />
                  <span>Ask Wealthy to Audit My Progress</span>
                </button>
              </div>

              {/* Add New Goal Form */}
              <form onSubmit={handleAddGoal} className={`p-4 rounded-2xl border space-y-2.5 ${
                isDarkMode ? 'bg-[#162340] border-[#1A2940]' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  <Plus size={12} className="text-blue-500" />
                  <span>Add Wealth Milestone or Commitment</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newGoalTitle}
                    onChange={(e) => setNewGoalTitle(e.target.value)}
                    placeholder="e.g. Save $500 monthly, rebalance stocks..."
                    className={`flex-1 px-3 py-2 text-xs font-bold rounded-xl border outline-none ${
                      isDarkMode 
                        ? 'bg-[#111E33] border-[#1A2940] text-white placeholder-slate-500' 
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                  />
                  <select
                    value={newGoalCategory}
                    onChange={(e: any) => setNewGoalCategory(e.target.value)}
                    className={`text-xs font-black rounded-xl border px-2 outline-none ${
                      isDarkMode ? 'bg-[#111E33] border-[#1A2940] text-slate-200' : 'bg-white border-slate-200 text-slate-800'
                    }`}
                  >
                    <option value="Portfolio">Portfolio</option>
                    <option value="Budget">Budget</option>
                    <option value="Savings">Savings</option>
                    <option value="Debt">Debt</option>
                  </select>
                  <button
                    type="submit"
                    disabled={!newGoalTitle.trim()}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-black transition-all active:scale-95"
                  >
                    Add
                  </button>
                </div>
              </form>

              {/* Tracked Goals List */}
              <div className="space-y-2">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
                  <span>Tracked Action Items</span>
                  <span>{trackedGoals.length} Total</span>
                </div>

                {trackedGoals.map((g) => (
                  <div
                    key={g.id}
                    className={`p-3 rounded-2xl border transition-all flex items-start justify-between gap-3 ${
                      g.completed
                        ? isDarkMode ? 'bg-emerald-950/20 border-emerald-500/20 text-slate-300' : 'bg-emerald-50/60 border-emerald-100 text-slate-700'
                        : isDarkMode ? 'bg-[#162340] border-[#1A2940] text-white' : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  >
                    <button
                      onClick={() => toggleGoal(g.id)}
                      className="mt-0.5 text-slate-400 hover:text-emerald-500 transition-colors shrink-0"
                    >
                      {g.completed ? (
                        <CheckCircle2 size={18} className="text-emerald-500" />
                      ) : (
                        <Circle size={18} />
                      )}
                    </button>

                    <div className="flex-1">
                      <p className={`text-xs font-bold leading-tight ${g.completed ? 'line-through text-slate-400' : ''}`}>
                        {g.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-400/20">
                          {g.category}
                        </span>
                        <span className="text-[9px] text-slate-400">
                          {g.completed ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => removeGoal(g.id)}
                      className="text-slate-400 hover:text-rose-500 p-1 rounded-lg transition-colors shrink-0"
                      title="Delete goal"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}

                {trackedGoals.length === 0 && (
                  <div className="py-8 text-center text-slate-400 text-xs font-bold">
                    No tracked goals yet. Add one above or ask Wealthy for recommendations!
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FinanceChatbot;
